import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";

import { api } from "@api/api";
import { filesStoreFn, useFileStore } from "@stores/fileStore";

import type { AxiosProgressEvent, CancelToken } from "axios";

import type { IApiResponse } from "@api";
import type { ID, IFileObject, IFileResponse } from "@interfaces";
import type { IFileMetaData } from "@stores/fileStore";

// import { uploadFileAPI } from '@api/file'
// import type { IUploadFileResponseType } from '@api/file/files'

export type IUploadMutationFn<T = unknown> = (
  data: File,
  progressCallBack?: (progressEvent: AxiosProgressEvent) => void,
  cancelToken?: CancelToken,
) => Promise<T>;

export interface IUploadFileVariable {
  file: File;
  fileID: ID;
}

interface IUseFileHandlerOptions {
  groupFilesID?: ID;
  initialFiles?: IFileObject[];
}

export interface IUploadMutationOptions<T> {
  onSuccess?: (data: T, variables: IUploadFileVariable) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onError?: (error: any, variables: IUploadFileVariable) => void;
}

/**
 * Use this when you want to use only the functionality of file handing.
 *
 * ATTENTION: This will NOT rerender the component when any files modify
 *
 * @param options
 *
 * @returns
 */
export function useFileHandlerFns(options?: IUseFileHandlerOptions) {
  const [localFileID] = useState(options?.groupFilesID || crypto.randomUUID());

  // rerendering when files isLoading flag has changed
  useFileStore((state) => state.isLoadingModifySignal);

  /**
   * @returns Map<ID, FileMetaData> or undefined
   */
  const _getFilesData = useCallback((): Map<ID, IFileMetaData> | undefined => {
    return filesStoreFn.files.get(localFileID);
  }, [localFileID]);

  useEffect(() => {
    const files = filesStoreFn.files;
    const groupFilesIsLoading = filesStoreFn.groupFilesIsLoading;

    // initial new set of filesMetadata

    if (!files.has(localFileID)) {
      files.set(localFileID, new Map());
      groupFilesIsLoading.set(localFileID, false);
    }

    // add initial files
    const initialFiles = options?.initialFiles?.filter(
      (item) => item !== undefined && item !== null,
    );

    if (initialFiles) {
      const existenceFileIDs = new Set(
        functionalities
          .getAllFiles()
          .filter((item) => !(item[1].file instanceof File))
          .map((item) => item[0]),
      );

      const initialFilesIDs = new Set(initialFiles.map((item) => item.id));

      const difference = initialFilesIDs.difference(existenceFileIDs);

      for (const element of difference) {
        const fileObject = initialFiles.find((item) => item.id === element);

        if (fileObject) privateFunctionalities._addFileObject(fileObject);
      }
      privateFunctionalities.forceUpdate();
    }

    // cancel every file uploading in component unmounting
    return () => {
      if (options?.groupFilesID !== undefined) return;

      functionalities.cancelAll();
      // and delete from the set of the groupOfFilesMetadata
      files.delete(localFileID);
      filesStoreFn.updateFilesSignal();

      groupFilesIsLoading.delete(localFileID);
      filesStoreFn.updateIsLoadingSignal();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   *
   * @returns Array of files with status 'uploading'
   */
  const getAllUploadingFiles = useCallback(() => {
    const metadata = _getFilesData();

    if (metadata) {
      return [...metadata].filter((item) => item[1].status === "uploading");
    }
    return [];
  }, [_getFilesData]);

  const privateFunctionalities = useMemo(() => {
    /**
     * Forces a re-render of the components when files modify.
     */
    function forceUpdate() {
      filesStoreFn.updateFilesSignal();
    }

    /**
     * Forces a re-render of the components when isLoading state modify.
     */
    function forceUpdateIsLoading() {
      filesStoreFn.updateIsLoadingSignal();
    }

    /**
     *
     * @returns Map<ID, boolean> or undefined
     */
    function _getFilesIsLoading() {
      return filesStoreFn.groupFilesIsLoading.get(localFileID);
    }

    /**
     * @param fn - function to iterate on values
     */
    function _iterateOnValues(fn: (value: IFileMetaData) => void) {
      const values = _getFilesData()?.values();

      if (values) {
        for (const value of values) {
          fn(value);
        }
      }
    }
    function _addFile(file: File): ID {
      const uuid = crypto.randomUUID();

      _getFilesData()?.set(uuid, {
        file,
        percent: 0,
        status: "idle",
        source: axios.CancelToken.source(),
        uploadedValue: 0,
      });
      return uuid;
    }

    function _addFileObject(file: IFileObject): ID | undefined {
      if (file === undefined || file === null) {
        return;
      }

      _getFilesData()?.set(file.id, {
        file,
        percent: 0,
        status: "submitted",
        source: axios.CancelToken.source(),
        uploadedValue: file.size,
      });
      return file.id;
    }
    /**
     * @param fn - function to iterate on keys
     */
    function _iterateOnKeys(fn: (key: ID) => void) {
      const keys = _getFilesData()?.keys();

      if (keys) {
        for (const key of keys) {
          fn(key);
        }
      }
    }

    /**
     * @param id - ID of the file
     * @param fn - function to update file metadata
     * @param rerender - boolean flag to force rerender
     */
    function update(
      id: ID,
      fn: (prevValue: IFileMetaData) => IFileMetaData,
      rerender = true,
    ) {
      const data = _getFilesData()?.get(id);

      if (data) {
        _getFilesData()?.set(id, fn(data));
        if (rerender) privateFunctionalities.forceUpdate();
      }
    }

    function updateIsLoading(value: boolean) {
      if (_getFilesIsLoading() !== value) {
        if (value) {
          filesStoreFn.groupFilesIsLoading.set(localFileID, true);
          forceUpdateIsLoading();
          return;
        }
        // otherwise
        if (getAllUploadingFiles().length === 0) {
          filesStoreFn.groupFilesIsLoading.set(localFileID, false);
          forceUpdateIsLoading();
        }
      }
    }

    return {
      _iterateOnKeys,
      _addFile,
      _addFileObject,
      _getFilesIsLoading,
      _iterateOnValues,
      forceUpdate,
      forceUpdateIsLoading,
      update,
      updateIsLoading,
    };
  }, [_getFilesData, getAllUploadingFiles, localFileID]);

  const functionalities = useMemo(() => {
    /**
     * @param file - File object
     * @return  ID of the file
     */
    function add(file: File | IFileObject): ID | undefined {
      const id =
        file instanceof File
          ? privateFunctionalities._addFile(file)
          : privateFunctionalities._addFileObject(file);

      privateFunctionalities.forceUpdate();
      return id;
    }

    /**
     * @param id - ID of the file to delete
     */
    function deleteFile(id: ID) {
      _getFilesData()?.get(id)?.source.cancel();
      _getFilesData()?.delete(id);
      privateFunctionalities.forceUpdate();
    }

    /**
     * @param id - ID of the file to cancel
     */
    function cancel(id: ID) {
      // cancel file upload
      _getFilesData()?.get(id)?.source.cancel();

      // update file status and refreshing cancel token
      privateFunctionalities.update(id, (prev) => ({
        ...prev,
        status: "error",
        source: axios.CancelToken.source(),
      }));
    }

    /**
     * @param fileID - ID of the file being uploaded
     * @param progressEvent - AxiosProgressEvent object
     */
    function onProgressCallback(fileID: ID, progressEvent: AxiosProgressEvent) {
      const { progress, loaded } = progressEvent;

      if (progress) {
        // calculate the uploaded percentages
        const percent = Math.round(progress * 100);

        // update the percent and rerender
        privateFunctionalities.update(
          fileID,
          (prev) => ({ ...prev, percent, uploadedValue: loaded }),
          false,
        );
        filesStoreFn.updateProgressSignal();
      }
    }

    /**
     * @param id - ID of the file to check for upload eligibility
     * @returns boolean
     */
    function _canUpload(id: ID) {
      const fileMetaData = _getFilesData()?.get(id);

      const file = fileMetaData?.file;

      if (
        fileMetaData === null ||
        fileMetaData === undefined ||
        file === null ||
        file === undefined
      )
        return;

      if (!(fileMetaData.status === "idle" || fileMetaData.status === "error"))
        return;

      if (!(file instanceof File)) return;

      return file;
    }

    /**
     * @param id - ID of the file to upload
     * @param mutationFn
     * @param options
     */
    function uploadFile<T = IApiResponse<IFileResponse>>(
      id: ID,
      mutationFn: IUploadMutationFn<T> = api.files
        .postFile as IUploadMutationFn<T>,
      options?: IUploadMutationOptions<T>,
    ) {
      return new Promise<{
        data: T;
        variables: IUploadFileVariable;
      }>((resolve, reject) => {
        const file = _canUpload(id);

        if (file === undefined) {
          reject(new Error("The file could not upload."));
          return;
        }

        const token = _getFilesData()?.get(id)?.source.token;
        const variables = { file, fileID: id };

        privateFunctionalities.update(id, (prev) => ({
          ...prev,
          status: "uploading",
          percent: 0,
        }));
        privateFunctionalities.updateIsLoading(true);
        mutationFn(
          file,
          (progressEvent) => {
            onProgressCallback(id, progressEvent);
          },
          token,
        )
          .then((data) => {
            privateFunctionalities.update(id, (prev) => ({
              ...prev,
              status: "success",
              // UUID: data.data.data
              UUID: crypto.randomUUID(),
            }));
            privateFunctionalities.updateIsLoading(false);
            options?.onSuccess?.(data, variables);
            resolve({ data, variables });
          })
          .catch((error: unknown) => {
            privateFunctionalities.update(id, (prev) => ({
              ...prev,
              status: "error",
            }));
            privateFunctionalities.updateIsLoading(false);
            options?.onError?.(error, variables);

            reject(
              new Error(`An error occurred in uploading file. Error: ${error}`),
            );
          });
      });
    }

    /**
     * Upload all files that are eligible for upload
     */
    function uploadAll<T = IApiResponse<IFileResponse>>(
      mutationFn: IUploadMutationFn<T> = api.files
        .postFile as IUploadMutationFn<T>,
      options?: IUploadMutationOptions<T>,
    ) {
      privateFunctionalities._iterateOnKeys((key) =>
        uploadFile(key, mutationFn, options),
      );
    }

    /**
     * Cancel upload for all files
     */
    function cancelAll() {
      privateFunctionalities._iterateOnKeys((key) => {
        cancel(key);
      });
    }

    /**
     * Cancel upload for all files
     */
    function deleteAll() {
      privateFunctionalities._iterateOnKeys((key) => {
        deleteFile(key);
      });
    }

    /**
     *
     * @returns Array of files with status 'idle', 'uploading', or 'error'
     */
    function getAllUnUploadedFiles() {
      const metadata = _getFilesData();

      if (metadata) {
        return [...metadata].filter((item) => item[1].status !== "success");
      }
      return [];
    }

    /**
     *
     * @returns Array of files with status 'success'
     */
    function getAllUploadedFiles() {
      const metadata = _getFilesData();

      if (metadata) {
        return [...metadata].filter((item) => item[1].status === "success");
      }
      return [];
    }

    /**
     *
     * @returns Array of files with status 'idle'
     */
    function getAllIdleFiles() {
      const metadata = _getFilesData();

      if (metadata) {
        return [...metadata].filter((item) => item[1].status === "idle");
      }
      return [];
    }

    /**
     *
     * @returns Array of files with status 'error'
     */
    function getAllCanceledFiles() {
      const metadata = _getFilesData();

      if (metadata) {
        return [...metadata].filter((item) => item[1].status === "error");
      }
      return [];
    }

    /**
     *
     * @returns Array of files
     */
    function getAllFiles() {
      const metadata = _getFilesData();

      if (metadata) {
        return [...metadata];
      }
      return [];
    }

    /**
     * cancel and clear all files
     */
    function clear() {
      cancelAll();
      _getFilesData()?.clear();
      privateFunctionalities.forceUpdate();
    }

    return {
      add,
      cancel,
      cancelAll,
      clear,
      deleteFile,
      deleteAll,
      getAllCanceledFiles,
      getAllFiles,
      getAllIdleFiles,
      getAllUnUploadedFiles,
      getAllUploadedFiles,
      uploadAll,
      uploadFile,
    };
  }, [_getFilesData, privateFunctionalities]);

  return useMemo(
    () => ({
      localFileID,
      getAllUploadingFiles,
      isLoading: filesStoreFn.groupFilesIsLoading.get(localFileID) || false,
      ...functionalities,
    }),
    [localFileID, getAllUploadingFiles, functionalities],
  );
}

/**
 * Use this when you want to use only the functionality of file handing.And rerender the component when files has modified.
 * @param options
 * @returns
 */
export function useFileHandler(options?: IUseFileHandlerOptions) {
  // rerendering when files modify
  useFileStore((state) => state.filesModifySignal);

  return useFileHandlerFns(options);
}
